import { useEffect, useMemo, useRef } from 'react';
import { PERMISSIONS } from '@eombor/contracts';
import { cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';

/**
 * Ruxsat matritsasi.
 *
 * Katalog **koddan** keladi (`@eombor/contracts` dagi `PERMISSIONS`), bazadan
 * emas: ruxsat kodi dasturning o'zi bilan birga tug'iladi va o'ladi. Bazada
 * faqat "qaysi rolda qaysi kod bor" degan bog'lanish turadi.
 */

interface GroupItem {
  code: string;
  label: string;
}

interface PermissionGroup {
  name: string;
  items: GroupItem[];
}

/** Katalogdagi tartib — chiqadigan ro'yxat doim shu tartibda bo'ladi. */
const ORDER = new Map<string, number>(PERMISSIONS.map((p, index) => [p.code, index]));

const GROUPS: PermissionGroup[] = (() => {
  const byName = new Map<string, PermissionGroup>();
  for (const permission of PERMISSIONS) {
    const group = byName.get(permission.group) ?? { name: permission.group, items: [] };
    group.items.push({ code: permission.code, label: permission.label });
    byName.set(permission.group, group);
  }
  return [...byName.values()];
})();

/**
 * Tanlangan kodlarni katalog tartibiga soladi. Shu tufayli `onChange` ning
 * natijasi bosish tartibiga bog'liq emas: bir xil to'plam — bir xil ro'yxat.
 * Katalogda yo'q (eskirgan) kod oxirida qoladi, o'chib ketmaydi.
 */
const sortByCatalog = (codes: Iterable<string>): string[] =>
  [...new Set(codes)].sort((a, b) => (ORDER.get(a) ?? Number.MAX_SAFE_INTEGER) - (ORDER.get(b) ?? Number.MAX_SAFE_INTEGER));

interface TriStateBoxProps {
  checked: boolean;
  mixed: boolean;
  label: string;
  disabled: boolean;
  onToggle: () => void;
}

/**
 * Uch holatli "hammasini tanlash". `indeterminate` — DOM xossasi, uni faqat
 * havola orqali qo'yish mumkin; ekran o'quvchi uchun esa `aria-checked="mixed"`
 * qo'shiladi.
 */
const TriStateBox = ({ checked, mixed, label, disabled, onToggle }: TriStateBoxProps) => {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current !== null) ref.current.indeterminate = mixed;
  }, [mixed]);

  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      aria-checked={mixed ? 'mixed' : undefined}
      aria-label={label}
      disabled={disabled}
      onChange={onToggle}
      className="size-4 shrink-0 accent-accent disabled:cursor-not-allowed"
    />
  );
};

export interface PermissionMatrixProps {
  value: string[];
  onChange: (next: string[]) => void;
  /** Tizim rollari uchun: hamma katakcha o'chiriladi, `onChange` chaqirilmaydi. */
  readOnly?: boolean;
}

export const PermissionMatrix = ({ value, onChange, readOnly = false }: PermissionMatrixProps) => {
  const selected = useMemo(() => new Set(value), [value]);

  const emit = (mutate: (draft: Set<string>) => void): void => {
    if (readOnly) return;
    const draft = new Set(selected);
    mutate(draft);
    onChange(sortByCatalog(draft));
  };

  return (
    <div className="grid gap-3 lg:grid-cols-2">
      {GROUPS.map((group) => {
        const chosen = group.items.filter((item) => selected.has(item.code)).length;
        const full = chosen === group.items.length;
        const partial = chosen > 0 && !full;

        return (
          <fieldset
            key={group.name}
            role="group"
            className={cn(
              'relative rounded-[var(--radius-lg)] border border-border bg-surface pb-2',
              // Guruh holati chap qirradagi belgi bilan: to'la — yashil,
              // qisman — ko'k. Uzoqdan qaraganda ham qaysi bo'lim ochilgani
              // ko'rinadi, har bir katakchani o'qib chiqish shart emas.
              full ? 'shadow-[var(--shadow-rail)]' : undefined,
              partial ? 'shadow-[var(--shadow-rail-info)]' : undefined,
            )}
          >
            {/* `legend` — `fieldset` ning **birinchi** farzandi: guruhning nomi
                aynan shundan hisoblanadi. "Hammasini tanlash" katakchasi esa
                nomga qo'shilib ketmasligi uchun mutlaq joylashadi. */}
            <legend className="px-4 pt-3 text-sm font-semibold tracking-tight text-text">
              {group.name}
            </legend>

            <label
              className={cn(
                'absolute top-3 right-3 flex items-center gap-2',
                readOnly ? 'cursor-not-allowed' : 'cursor-pointer',
              )}
            >
              <span className="tabular text-xs text-text-faint">
                {t('rbac.groupCount', { selected: chosen, total: group.items.length })}
              </span>
              <TriStateBox
                checked={full}
                mixed={partial}
                disabled={readOnly}
                label={t('rbac.selectAll', { group: group.name })}
                onToggle={() =>
                  emit((draft) => {
                    for (const item of group.items) {
                      if (full) draft.delete(item.code);
                      else draft.add(item.code);
                    }
                  })
                }
              />
            </label>

            <ul className="mt-1 flex flex-col">
              {group.items.map((item) => (
                <li key={item.code}>
                  <label
                    className={cn(
                      'flex items-start gap-3 px-4 py-2',
                      'transition-[background-color] duration-[var(--motion-fast)]',
                      'ease-[var(--ease-standard)]',
                      readOnly ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-surface-2',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(item.code)}
                      disabled={readOnly}
                      onChange={() =>
                        emit((draft) => {
                          if (draft.has(item.code)) draft.delete(item.code);
                          else draft.add(item.code);
                        })
                      }
                      className="mt-0.5 size-4 shrink-0 accent-accent disabled:cursor-not-allowed"
                    />
                    <span className="flex min-w-0 flex-col">
                      <span className="text-sm text-text">{item.label}</span>
                      <span className="tabular text-xs text-text-faint">{item.code}</span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
        );
      })}
    </div>
  );
};
